import React, { useEffect, useMemo, useState } from 'react';

export type ClubTourStep = {
  id: string;
  title: string;
  body: string;
};

const DEFAULT_STEPS: ClubTourStep[] = [
  {
    id: 'welcome',
    title: 'خوش آمدید به باشگاه مشتریان',
    body: 'این صفحه سه سگمنت VIP، فراموش‌شده و آستانه ریزش را از پذیرش واقعی HIS نشان می‌دهد.',
  },
  {
    id: 'period',
    title: 'بازه شمارش مراجعات',
    body: '۱۲ ماه، ۲، ۳ یا ۵ سال و همه سال‌ها (از ۱۳۹۲) را برای شمارش پذیرش انتخاب کنید.',
  },
  {
    id: 'segments',
    title: 'سگمنت‌های مشتری',
    body: 'کارت‌های VIP، فراموش‌شده و آستانه ریزش را بزنید تا لیست همان گروه باز شود.',
  },
  {
    id: 'table',
    title: 'لیست و جزئیات',
    body: 'روی هر ردیف کلیک کنید تا پذیرش‌های HIS همان مشتری را ببینید. فیلترهای آزاد در منوی «گزارش‌ساز» اضافه می‌شود.',
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  steps?: ClubTourStep[];
};

const CustomerClubTour: React.FC<Props> = ({ open, onClose, steps = DEFAULT_STEPS }) => {
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState<DOMRect | null>(null);
  const step = steps[index];

  const progress = useMemo(
    () => `${index + 1} از ${steps.length}`,
    [index, steps.length]
  );

  useEffect(() => {
    if (!open) return;
    setIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open || !step) return;
    const update = () => {
      if (step.id === 'welcome') {
        setSpot(null);
        return;
      }
      const el = document.querySelector(`[data-tour="${step.id}"]`) as HTMLElement | null;
      if (!el) {
        setSpot(null);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setSpot(el.getBoundingClientRect());
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, step]);

  if (!open || !step) return null;

  const pad = 8;
  const highlight = spot
    ? {
        top: Math.max(8, spot.top - pad),
        left: Math.max(8, spot.left - pad),
        width: spot.width + pad * 2,
        height: spot.height + pad * 2,
      }
    : null;

  return (
    <div className="fixed inset-0 z-[70]" style={{ direction: 'rtl' }}>
      <div className="absolute inset-0 bg-slate-950/55" onClick={onClose} aria-hidden />
      {highlight && (
        <div
          className="pointer-events-none absolute rounded-2xl ring-2 ring-indigo-300 shadow-[0_0_0_9999px_rgba(15,23,42,0.55)] transition-all duration-300"
          style={{
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
          }}
        />
      )}
      <div className="absolute bottom-6 left-1/2 z-[71] w-[min(28rem,calc(100%-1.5rem))] -translate-x-1/2 rounded-2xl border border-indigo-100 bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-indigo-600">{progress}</p>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-800"
          >
            رد کردن
          </button>
        </div>
        <h3 className="text-lg font-bold text-gray-900">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">{step.body}</p>
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:opacity-40"
          >
            قبلی
          </button>
          {index < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => setIndex((i) => Math.min(steps.length - 1, i + 1))}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
            >
              بعدی
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
            >
              شروع کار
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerClubTour;
